'use client';

import { useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';

import type { CustomerOrder } from '@eliadmin/core/src/api/customers/customers-types';
import { OrderCard } from '@eliadmin/core/src/components/nt/account/orders/order-card';
import { OrdersMessage } from '@eliadmin/core/src/components/nt/account/orders/order-parts';
import {
  ORDER_FILTERS,
  orderStage,
  stageFilter,
  type OrderFilter,
} from '@eliadmin/core/src/components/nt/account/orders/order-stage';
import { useOrderTexts } from '@eliadmin/core/src/components/nt/account/orders/order-texts';
import {
  orderNumber,
  searchText,
  useCustomerOrders,
} from '@eliadmin/core/src/components/nt/account/orders/orders-data';
import { Icon } from '@eliadmin/core/src/components/nt/ui/icon';
import Link from '@eliadmin/core/src/components/link';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { GenericLoader } from '@eliadmin/core/src/components/ui/generic-loader';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { fillTemplate, splitOptions } from '@eliadmin/core/src/utils/nt/text';
import { cn } from '@eliadmin/core/src/utils/style-utils';

/** The design lists four orders; „Zobraziť staršie objednávky (N)“ adds the next ten. */
const FIRST = 4;
const MORE = 10;

type ListedOrder = { order: CustomerOrder; filter: Exclude<OrderFilter, 'all'>; text: string };

/** Figma „Heading“: H2 title and Body/L text, 8 px apart, 8 px more space under it. */
function OrdersHeading() {
  const { t } = useTranslations();
  const { account } = useOrderTexts();

  return (
    <header className="flex flex-col gap-2 pb-2">
      <h1 className="nt-h2 text-[#0D110D]">{t('text257')}</h1>
      {account.ordersText ? <p className="nt-body-l text-[#363D36]">{account.ordersText}</p> : null}
    </header>
  );
}

/**
 * Figma „Chip“: 40 px, radius 12, Body/M Semibold, 16 px padding; the selected one black. Figma
 * counts the 1 px stroke of the unselected chips into their width, the black chip has none.
 */
function FilterChip({
  selected,
  onSelect,
  children,
}: {
  selected: boolean;
  onSelect: () => void;
  children: string;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onSelect}
      className={cn(
        'nt-body-m-semibold h-10 shrink-0 whitespace-nowrap rounded-xl border transition-colors focus-visible:shadow-focus focus-visible:outline-none',
        selected
          ? 'border-[#0D110D] bg-[#0D110D] px-[15px] text-white'
          : 'border-[#E2E5DF] bg-white px-4 text-[#0D110D] hover:bg-[#F5F6F3]',
      )}
    >
      {children}
    </button>
  );
}

/**
 * Figma „Search“: 44 px, radius 16, 18 px magnifier, Body/M (16 px on phones – iOS zooms into
 * smaller inputs). The placeholder is the admin text and also the field's name.
 */
function SearchField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="flex h-11 w-full items-center gap-2.5 rounded-2xl border border-[#E2E5DF] bg-white px-[13px] transition-colors focus-within:border-[#127A26] focus-within:shadow-focus @[50rem]:w-80">
      <Icon name="magnifying-glass" size={18} className="text-[#5D655D]" />
      <input
        type="search"
        value={value}
        onChange={event => onChange(event.target.value)}
        placeholder={label}
        aria-label={label}
        className="nt-body-m min-w-0 flex-1 bg-transparent text-[#0D110D] outline-none placeholder:text-[#8F978E] max-md:text-base"
      />
    </label>
  );
}

/**
 * Figma „Moje objednávky“ (207:11671): heading, the toolbar (filter chips with counts, search by
 * order number or product name), the order cards and „Zobraziť staršie objednávky (N)“. The whole
 * list is loaded once; filtering and search run on the client. The chips scroll sideways on phones
 * (to the screen edge); the search sits beside them from a 50rem column, under them narrower.
 */
export function OrdersList() {
  const { t } = useTranslations();
  const { account } = useOrderTexts();
  const { data: orders, isPending, isError, refetch, isRefetching } = useCustomerOrders();
  const [filter, setFilter] = useState<OrderFilter>('all');
  const [query, setQuery] = useState('');
  const [visible, setVisible] = useState(FIRST);
  const list = useRef<HTMLOListElement>(null);

  const listed = useMemo<ListedOrder[]>(
    () =>
      (orders ?? []).map(order => ({
        order,
        filter: stageFilter(orderStage(order.status)),
        text: searchText([orderNumber(order), ...order.items.map(item => item.name)].join(' ')),
      })),
    [orders],
  );

  // „Všetky | Aktívne | Doručené | Zrušené“ – the labels by position.
  const filters = splitOptions(account.orderFilters)
    .slice(0, ORDER_FILTERS.length)
    .map((label, index) => ({ value: ORDER_FILTERS[index], label }));
  const count = (value: OrderFilter) =>
    value === 'all' ? listed.length : listed.filter(item => item.filter === value).length;

  const needle = searchText(query);
  const matches = listed.filter(
    item => (filter === 'all' || item.filter === filter) && item.text.includes(needle),
  );
  const older = matches.length - visible;

  const choose = (value: OrderFilter) => {
    setFilter(value);
    setVisible(FIRST);
  };
  const search = (value: string) => {
    setQuery(value);
    setVisible(FIRST);
  };
  const reset = () => {
    choose('all');
    setQuery('');
  };
  // Keyboard users continue at the first card that appeared.
  const showOlder = () => {
    const first = visible;
    flushSync(() => setVisible(first + MORE));
    list.current?.children[first]?.querySelector<HTMLElement>('a')?.focus();
  };

  let content;
  if (isPending) {
    content = <GenericLoader className="h-80" />;
  } else if (isError) {
    content = (
      <OrdersMessage
        icon="warning-circle"
        title={t('text594')}
        alert
        action={
          <Button variant="outline" onClick={() => refetch()} isLoading={isRefetching}>
            {t('text595')}
          </Button>
        }
      />
    );
  } else if (!listed.length) {
    content = (
      <OrdersMessage
        icon="package"
        title={t('text445')}
        action={
          <Button asChild variant="primary">
            <Link href={ROUTES.HOME() || '/'} prefetch={false}>
              {t('text208')}
            </Link>
          </Button>
        }
      />
    );
  } else {
    content = (
      <>
        <div className="flex flex-col gap-4 @[50rem]:flex-row @[50rem]:items-center @[50rem]:justify-between">
          <div className="hide-scroll-bar -mx-4 -my-1 flex gap-2 overflow-x-auto px-4 py-1 sm:-mx-6 sm:px-6 md:mx-0 md:flex-wrap md:overflow-visible md:px-0">
            {filters.map(option => (
              <FilterChip
                key={option.value}
                selected={filter === option.value}
                onSelect={() => choose(option.value)}
              >
                {`${option.label} (${count(option.value)})`}
              </FilterChip>
            ))}
          </div>
          {account.orderSearch ? (
            <SearchField label={account.orderSearch} value={query} onChange={search} />
          ) : null}
        </div>

        {matches.length ? (
          <ol ref={list} className="flex flex-col gap-4">
            {matches.slice(0, visible).map(({ order }) => (
              <li key={order.id}>
                <OrderCard order={order} />
              </li>
            ))}
          </ol>
        ) : (
          <OrdersMessage
            icon="magnifying-glass"
            title={t('text752')}
            action={
              <Button variant="outline" onClick={reset}>
                {t('text2476')}
              </Button>
            }
          />
        )}

        {older > 0 ? (
          <div className="flex justify-center pt-2">
            <Button variant="outline" onClick={showOlder} className="max-md:w-full">
              {fillTemplate(account.olderOrders, { pocet: older })}
            </Button>
          </div>
        ) : null}
      </>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <OrdersHeading />
      {content}
    </div>
  );
}
